import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useProducerTerms } from '@/hooks/useProducerTerms';
import { Skeleton } from '@/components/ui/skeleton';

interface ProducerTermsGateProps {
  producerId: string | undefined;
  children: ReactNode;
}

/**
 * Gate that redirects to the terms acceptance page if terms are not accepted.
 * The terms page renders WITHOUT portal chrome (bottom nav / FAB) because
 * its path is listed in AppShell's HIDE_CHROME_PATTERNS.
 */
export function ProducerTermsGate({ producerId, children }: ProducerTermsGateProps) {
  const location = useLocation();
  const { isLoading, isAccepted } = useProducerTerms(producerId);

  // Opt-in bypass for local E2E runs (controlled via Vite env)
  const disableE2ETerms = (import.meta.env.VITE_DISABLE_TERMS_FOR_E2E ?? '') === '1';
  if (disableE2ETerms) {
    // eslint-disable-next-line no-console
    console.debug('[E2E] ProducerTermsGate bypass active');
    return <>{children}</>;
  }

  // Skip gate for demo mode or missing producerId
  if (producerId === 'demo' || !producerId) {
    return <>{children}</>;
  }

  // Loading state — minimal spinner, no overlay
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Skeleton className="h-12 w-12 rounded-full" />
      </div>
    );
  }

  // Terms accepted → render children normally
  if (isAccepted) {
    return <>{children}</>;
  }

  // Redirect to terms page, passing current path as returnTo
  const currentPath = location.pathname + location.search;
  const returnTo = encodeURIComponent(currentPath);
  return (
    <Navigate
      to={`/app/producer/farm/${producerId}/terms?returnTo=${returnTo}`}
      replace
    />
  );
}
